"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useTableColumnDefs = exports.useGetStandardFieldColumnDef = exports.useGetCustomFieldColumnDef = exports.useColumnPropsFromLocalStorage = exports.useColumnKeysInDefaultOrder = void 0;
var _hooks = require("../../../state/redux/hooks");
var _issueTypes = require("../../../state/redux/ducks/issueTypes");
var _issueRootCauses = require("../../../state/redux/ducks/issueRootCauses");
var _issueCustomFields = require("../../../state/redux/ducks/issueCustomFields");
var _issueCustomFieldMappings = require("../../../state/redux/ducks/issueCustomFieldMappings");
var _cellRenderers = require("./cellRenderers");
var _referencesCellRenderer = require("./referencesCellRenderer");
var _dataGetters = require("./dataGetters");
var _columnKeys = require("./columnKeys");
var _useLocalStorage = require("../../../hooks/useLocalStorage");
var _projectMembersHooks = require("../../../utils/hooks/projectMembersHooks");
var _oxygenUtils = require("../../../utils/data/oxygenUtils");
var _react = require("react");
var _featureFlags = require("../../../utils/featureFlags");
var _useTranslations = require("../../../utils/i18n/useTranslations");
var _DeletedIssuesSpaceContext = require("../../../DeletedIssuesSpaceContext");
var _useLocalizedDecimalSeparator = require("../../../hooks/useLocalizedDecimalSeparator");
const getBaseColumnProps = columnKey => {
  return {
    key: columnKey,
    resizable: true,
    minWidth: 120,
    width: 120,
    title: columnKey
  };
};

/**
 * returns column definitions that don't need additional context
 * @TODO: add stamp, start date, and comments once they exist in the backend
 */
const useStaticColumnPropsByColumnKey = () => {
  const {
    T
  } = (0, _useTranslations.useTranslations)();
  const isDeletedSpaceActive = (0, _react.useContext)(_DeletedIssuesSpaceContext.DeletedIssuesSpaceContext);
  return (0, _react.useMemo)(() => {
    const columns = new Map([[_columnKeys.ISSUE_COLUMN_KEYS.ID, {
      title: T.translate("issueAttributes.displayId"),
      resizable: false,
      width: 100,
      cellRenderer: _ref => {
        let {
          cellData
        } = _ref;
        return "#".concat(cellData);
      },
      toggleable: false,
      hidden: false
    }], [_columnKeys.ISSUE_COLUMN_KEYS.TITLE, {
      title: T.translate("issueAttributes.title"),
      width: 285,
      flexGrow: 1,
      hidden: false
    }], [_columnKeys.ISSUE_COLUMN_KEYS.STATUS, {
      title: T.translate("issueAttributes.status"),
      resizable: false,
      cellRenderer: _cellRenderers.statusCellRenderer,
      width: 150,
      toggleable: false,
      hidden: false
    }], [_columnKeys.ISSUE_COLUMN_KEYS.ISSUE_TYPE_CATEGORY, {
      width: 150,
      title: T.translate("issueAttributes.issueTypeCategory"),
      hidden: true
    }], [_columnKeys.ISSUE_COLUMN_KEYS.ISSUE_TYPE, {
      width: 150,
      title: T.translate("issueAttributes.issueType"),
      cellRenderer: _cellRenderers.typeCellRenderer,
      hidden: false
    }], [_columnKeys.ISSUE_COLUMN_KEYS.DESCRIPTION, {
      width: 120,
      title: T.translate("issueAttributes.description"),
      hidden: true
    }], [_columnKeys.ISSUE_COLUMN_KEYS.LBS_LOCATION, {
      title: T.translate("issueAttributes.location"),
      width: 120,
      cellRenderer: _cellRenderers.locationCellRenderer,
      hidden: true
    }], [_columnKeys.ISSUE_COLUMN_KEYS.LOCATION_DETAILS, {
      title: T.translate("issueAttributes.locationDetails"),
      width: 120,
      hidden: true
    }], [_columnKeys.ISSUE_COLUMN_KEYS.ASSIGNED_TO, {
      width: 200,
      title: T.translate("issueAttributes.assignedTo"),
      // the person, group, or company that is responsible for the issue
      hidden: false
    }], [_columnKeys.ISSUE_COLUMN_KEYS.WATCHERS, {
      width: 200,
      title: T.translate("issueAttributes.watchers"),
      // the people that are watching the issue
      hidden: true
    }], [_columnKeys.ISSUE_COLUMN_KEYS.COMPANY, {
      width: 200,
      title: T.translate("issueAttributes.company"),
      // the company of the assignee that is responsible for the issue
      hidden: true
    }], [_columnKeys.ISSUE_COLUMN_KEYS.START_DATE, {
      width: 220,
      title: T.translate("issueAttributes.startDate"),
      //the date + time an issue starts
      cellRenderer: _cellRenderers.dateCellRenderer,
      hidden: false
    }], [_columnKeys.ISSUE_COLUMN_KEYS.DUE_DATE, {
      width: 220,
      title: T.translate("issueAttributes.dueDate"),
      //the date + time an issue is required to be complete
      cellRenderer: _cellRenderers.dueDateCellRenderer,
      hidden: false
    }], [_columnKeys.ISSUE_COLUMN_KEYS.ROOT_CAUSE, {
      width: 160,
      title: T.translate("issueAttributes.rootCause"),
      hidden: true
    }], [_columnKeys.ISSUE_COLUMN_KEYS.PLACEMENT, {
      width: 200,
      title: T.translate("issueAttributes.placement"),
      cellRenderer: _cellRenderers.placementCellRenderer,
      hidden: false
    }], [_columnKeys.ISSUE_COLUMN_KEYS.IS_PUBLISHED, {
      width: 120,
      title: T.translate("filterPanel.publishedOptions.published"),
      hidden: true,
      cellRenderer: _ref2 => {
        let {
          cellData
        } = _ref2;
        return cellData ? T.translate("filterPanel.publishedOptions.published") : T.translate("filterPanel.publishedOptions.unpublished");
      }
    }], [_columnKeys.ISSUE_COLUMN_KEYS.CREATED_BY, {
      width: 120,
      title: T.translate("issueAttributes.createdBy"),
      hidden: true
    }], [_columnKeys.ISSUE_COLUMN_KEYS.CREATED_AT, {
      width: 120,
      title: T.translate("issueAttributes.createdAt"),
      //the date + time an issue was created
      cellRenderer: _cellRenderers.datetimeCellRenderer,
      hidden: true
    }], [_columnKeys.ISSUE_COLUMN_KEYS.UPDATED_BY, {
      width: 120,
      title: T.translate("issueAttributes.updatedBy"),
      hidden: true
    }], [_columnKeys.ISSUE_COLUMN_KEYS.UPDATED_AT, {
      width: 120,
      title: T.translate("issueAttributes.updatedAt"),
      //the date + time an issue was last updated
      cellRenderer: _cellRenderers.datetimeCellRenderer,
      hidden: true
    }], [_columnKeys.ISSUE_COLUMN_KEYS.REFERENCES, {
      width: 160,
      title: T.translate("issueAttributes.references"),
      cellRenderer: _referencesCellRenderer.referencesCellRenderer,
      hidden: true
    }]]);
    if (isDeletedSpaceActive) {
      columns.set(_columnKeys.ISSUE_COLUMN_KEYS.DELETED_BY, {
        width: 160,
        title: T.translate("issueAttributes.deletedBy"),
        hidden: false
      });
      columns.set(_columnKeys.ISSUE_COLUMN_KEYS.DELETED_ON, {
        width: 160,
        title: T.translate("issueAttributes.deletedAt"),
        cellRenderer: _cellRenderers.datetimeCellRenderer,
        hidden: false
      });
    }
    return columns;
  }, [T, isDeletedSpaceActive]);
};

/**
 * Return a map of column keys to the props that need to be populated by data provided by an outer context.
 * This function uses hooks to marshal the additional data required by those dataGetters.
 *
 * (e.g. we need to get all the issue types in order to create a data getter that resolves the issue type column)
 */
const useContextuallyHydratedColumnProps = () => {
  const {
    T
  } = (0, _useTranslations.useTranslations)();
  const issueTypeEntitiesMap = (0, _hooks.useSelector)(_issueTypes.getIssueTypeEntitiesMap);
  const issueRootCauseEntitiesMap = (0, _hooks.useSelector)(_issueRootCauses.getIssueRootCausesEntitiesMap);
  const usersEntitiesMap = (0, _projectMembersHooks.useUsersEntitiesMapByOxygenId)();
  const rolesEntitiesMap = (0, _projectMembersHooks.useRolesEntitiesMapByOxygenId)();
  const companiesEntitiesMap = (0, _projectMembersHooks.useCompaniesEntitiesMapByOxygenId)();
  const companyIdToUserNameCountMap = (0, _oxygenUtils.useGetCompanyIdToUserNameCountMap)(Object.values(usersEntitiesMap));
  const issueTypeDataGetter = (0, _dataGetters.getDataGetterForIssueType)({
    issueTypeEntitiesMap
  });
  const issueTypeCategoryDataGetter = (0, _dataGetters.getDataGetterForIssueTypeCategory)({
    issueTypeEntitiesMap
  });
  const projectMemberDataGetter = (0, _dataGetters.getProjectMemberDataGetter)({
    usersEntitiesMap,
    rolesEntitiesMap,
    companiesEntitiesMap,
    companyIdToUserNameCountMap,
    T
  });
  const multipleProjectMemberDataGetter = (0, _dataGetters.getMultipleProjectMembersDataGetter)({
    usersEntitiesMap,
    rolesEntitiesMap,
    companiesEntitiesMap,
    companyIdToUserNameCountMap,
    T
  });
  const projectMemberCompanyDataGetter = (0, _dataGetters.getProjectMemberCompanyDataGetter)({
    usersEntitiesMap,
    companiesEntitiesMap
  });
  return new Map([[_columnKeys.ISSUE_COLUMN_KEYS.ISSUE_TYPE_CATEGORY, {
    dataGetter: issueTypeCategoryDataGetter
  }], [_columnKeys.ISSUE_COLUMN_KEYS.ISSUE_TYPE, {
    dataGetter: issueTypeDataGetter
  }], [_columnKeys.ISSUE_COLUMN_KEYS.ROOT_CAUSE, {
    dataGetter: (0, _dataGetters.getDataGetterForRootCause)({
      issueRootCauseEntitiesMap
    })
  }], [_columnKeys.ISSUE_COLUMN_KEYS.CREATED_BY, {
    dataGetter: projectMemberDataGetter
  }], [_columnKeys.ISSUE_COLUMN_KEYS.ASSIGNED_TO, {
    dataGetter: projectMemberDataGetter
  }], [_columnKeys.ISSUE_COLUMN_KEYS.DELETED_BY, {
    dataGetter: projectMemberDataGetter
  }], [_columnKeys.ISSUE_COLUMN_KEYS.WATCHERS, {
    dataGetter: multipleProjectMemberDataGetter
  }], [_columnKeys.ISSUE_COLUMN_KEYS.COMPANY, {
    dataGetter: projectMemberCompanyDataGetter
  }], [_columnKeys.ISSUE_COLUMN_KEYS.UPDATED_BY, {
    dataGetter: projectMemberDataGetter
  }]]);
};
const useColumnKeysInDefaultOrder = () => {
  const {
    value: showReferenceCountInIssuesTable
  } = (0, _featureFlags.useFeatureFlag)(_featureFlags.FeatureFlag.ShowReferenceCountInIssueTable);
  const isDeletedSpaceActive = (0, _react.useContext)(_DeletedIssuesSpaceContext.DeletedIssuesSpaceContext);
  return [_columnKeys.ISSUE_COLUMN_KEYS.ID, _columnKeys.ISSUE_COLUMN_KEYS.TITLE, _columnKeys.ISSUE_COLUMN_KEYS.STATUS, _columnKeys.ISSUE_COLUMN_KEYS.ISSUE_TYPE_CATEGORY, _columnKeys.ISSUE_COLUMN_KEYS.ISSUE_TYPE, _columnKeys.ISSUE_COLUMN_KEYS.DESCRIPTION, _columnKeys.ISSUE_COLUMN_KEYS.ASSIGNED_TO, isDeletedSpaceActive && _columnKeys.ISSUE_COLUMN_KEYS.DELETED_BY, isDeletedSpaceActive && _columnKeys.ISSUE_COLUMN_KEYS.DELETED_ON, _columnKeys.ISSUE_COLUMN_KEYS.WATCHERS, _columnKeys.ISSUE_COLUMN_KEYS.COMPANY, _columnKeys.ISSUE_COLUMN_KEYS.LBS_LOCATION, _columnKeys.ISSUE_COLUMN_KEYS.LOCATION_DETAILS, _columnKeys.ISSUE_COLUMN_KEYS.DUE_DATE, _columnKeys.ISSUE_COLUMN_KEYS.START_DATE, _columnKeys.ISSUE_COLUMN_KEYS.PLACEMENT, _columnKeys.ISSUE_COLUMN_KEYS.ROOT_CAUSE, _columnKeys.ISSUE_COLUMN_KEYS.IS_PUBLISHED, _columnKeys.ISSUE_COLUMN_KEYS.CREATED_BY, _columnKeys.ISSUE_COLUMN_KEYS.CREATED_AT, _columnKeys.ISSUE_COLUMN_KEYS.UPDATED_BY, _columnKeys.ISSUE_COLUMN_KEYS.UPDATED_AT, showReferenceCountInIssuesTable && _columnKeys.ISSUE_COLUMN_KEYS.REFERENCES].filter(Boolean);
};
exports.useColumnKeysInDefaultOrder = useColumnKeysInDefaultOrder;
const useColumnPropsFromLocalStorage = () => {
  const localStorageKey = (0, _useLocalStorage.useLocalStorageKey)(_useLocalStorage.LocalStorageKeys.ISSUE_LOG_COLUMN_SETTINGS);
  // the bim360 matrix component uses localstorage
  // by appending "_columnsState" to its `storageKey` prop,
  // so we can access it that way
  const localStorageProps = (0, _useLocalStorage.getLocalStorageByKey)(localStorageKey + "_columnsState");
  return localStorageProps || {};
};
exports.useColumnPropsFromLocalStorage = useColumnPropsFromLocalStorage;
const useGetStandardFieldColumnDef = () => {
  const contextuallyHydratedColumnProps = useContextuallyHydratedColumnProps();
  const localStorageProps = useColumnPropsFromLocalStorage();
  const STATIC_COLUMN_PROPS_BY_COLUMN_KEY = useStaticColumnPropsByColumnKey();
  return (0, _react.useCallback)((key, index) => {
    return {
      ...getBaseColumnProps(key),
      position: index,
      dataKey: _columnKeys.ISSUE_COLUMN_DATA_KEYS[key],
      sortable: !!_columnKeys.ISSUE_COLUMN_SORT_ATTRS[key],
      ...STATIC_COLUMN_PROPS_BY_COLUMN_KEY.get(key),
      ...contextuallyHydratedColumnProps.get(key),
      ...localStorageProps[key]
    };
  }, [contextuallyHydratedColumnProps, localStorageProps, STATIC_COLUMN_PROPS_BY_COLUMN_KEY]);
};
exports.useGetStandardFieldColumnDef = useGetStandardFieldColumnDef;
const useGetCustomFieldColumnDef = () => {
  const standardColumnKeys = useColumnKeysInDefaultOrder();
  const localStorageProps = useColumnPropsFromLocalStorage();
  const customFieldEntitiesMap = (0, _hooks.useSelector)(_issueCustomFields.getCustomFieldEntitiesMap);
  const {
    formatLocalizedNumber
  } = (0, _useLocalizedDecimalSeparator.useLocalizedDecimalSeparator)();
  const customFieldDataGetter = (0, _dataGetters.getCustomFieldDataGetter)({
    customFieldEntitiesMap,
    // dataGetter cant use hooks
    helperFunctions: {
      formatLocalizedNumber
    }
  });
  const {
    value: shouldSortCustomFields
  } = (0, _featureFlags.useFeatureFlag)(_featureFlags.FeatureFlag.CustomFieldSorting);
  return (0, _react.useCallback)((key, index) => {
    return {
      ...getBaseColumnProps(key),
      dataGetter: customFieldDataGetter,
      title: customFieldEntitiesMap[key].title,
      position: index + standardColumnKeys.length,
      dataKey: "customAttributes[".concat(key, "]"),
      sortable: shouldSortCustomFields,
      hidden: true,
      ...localStorageProps[key]
    };
  }, [localStorageProps, customFieldDataGetter, customFieldEntitiesMap, standardColumnKeys.length, shouldSortCustomFields]);
};

/**
 * Unfortunately, the matrix table has some buggy edge cases with reordering columns.
 *
 * We can get around all of that by making sure to include the local storage data
 * that it generates when we generate the columns props:
 * that way the columns we pass in as props will always be in sync with local storage,
 * (and won't accidentallty override it!)
 */
exports.useGetCustomFieldColumnDef = useGetCustomFieldColumnDef;
const useTableColumnDefs = () => {
  const mappedCustomAttributeIdSet = (0, _hooks.useSelector)(_issueCustomFieldMappings.getMappedCustomAttributeIdSet);
  const CUSTOM_FIELD_KEYS = (0, _hooks.useSelector)(_issueCustomFields.getOrderedCustomFields).map(customField => customField.id).filter(id => mappedCustomAttributeIdSet.has(id));
  const getStandardFieldColumnDef = useGetStandardFieldColumnDef();
  const getCustomFieldColumnDef = useGetCustomFieldColumnDef();
  const standardFieldColumnKeys = useColumnKeysInDefaultOrder();
  const standardFieldColumns = standardFieldColumnKeys.map(getStandardFieldColumnDef);
  const customFieldColumns = CUSTOM_FIELD_KEYS.map(getCustomFieldColumnDef);
  return [...standardFieldColumns, ...customFieldColumns].sort((columnA, columnB) => columnA.position > columnB.position ? 1 : -1);
};
exports.useTableColumnDefs = useTableColumnDefs;
//# sourceMappingURL=useTableColumnDefs.js.map